import {take, takeUntil} from 'rxjs/operators';
import { MappingAction, MappingMode } from './../../../shared/enum/mapping-mode.enum';
import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import { UserApi } from '../../../api';
import { CategoryDataService } from '../../../api/category-data.service';
import { CategoryMappingOption } from '../../../shared/models/category-data.model';
import { ExtraFilteringOptions, LoadOptions } from '../../../shared/models/shared.model';
import {GridSetting, GridSettings, GridState, User} from '../../../shared';
import { Subject } from 'rxjs';
import {FilterBuilderService} from '../../../service/filter-builder.service';
import { LoaderService } from '../../../core/loader/loader.service';

@Component({
  selector: 'app-category-mapping',
  templateUrl: './category-mapping.component.html',
})
export class CategoryMappingComponent implements OnInit, OnDestroy {
  @ViewChild('categoryMappingDataGrid') categoryMappingDataGrid: DxDataGridComponent;
  @ViewChild('categoryMasterDataGrid') categoryMasterDataGrid: DxDataGridComponent;

  public categoryMappingDataSource = {};
  public categoryMasterDataSource = {};

  public categoryMappingSelectedRowMap = [];
  public categoryMasterSelectedRowMap = [];
  public extraFilteringOptions: ExtraFilteringOptions[] = [
	{ column: 'Active', value: false },
  ];

  public currentUser: User;
  public searchText;

  dataProviderCodeHeaderDataSource;
  companyNamedHeaderDataSource;
  totalCountHeaderDataSource;
  newHeaderDataSource;
  mapHeaderDataSource;
  saveHeaderDataSource;
  omitHeaderDataSource;
  inconsistencyHeaderDataSource;
  categoryIdHeaderDataSource;
  subcategoryFunctionNameHeaderDataSource;
  subcategoryFormNameHeaderDataSource;

  createdByHeaderDataSource;
  createdOnHeaderDataSource;
  modifiedByHeaderDataSource;
  modifiedOnHeaderDataSource;
  reviewedByHeaderDataSource;
  reviewedOnHeaderDataSource;

  mappingModeHeaderDataSource;

  masterCategoryNameDataSource;
  masterSubcategoryFunctionNameHeaderDataSource;
  mappingDataGridCurrentPage: number;

  masterSubcategoryFormNameHeaderDataSource;
  private _destroyed$ = new Subject();
  private isCategoryMappingDataLoading: boolean;
  private isCategoryMasterDataLoading: boolean;

  public isLoadCategoryMappingGridDataSource: boolean;
  public isLoadCategoryMasterGridDataSource: boolean;
  private readonly mappingGridName: string = 'categoryMappingDataGrid';
  private readonly masterGridName: string = 'categoryMasterDataGrid';
  private readonly pageName: string = 'categoryMapping';
  private currentCategoryMappingDataGridState: GridState;
  private currentCategoryMasterDataGridState: GridState;
  private categoryMappingDataGridSettings: GridSettings;
  private categoryMasterDataGridSettings: GridSettings;
  private isCategoryMappingDataGridSettingLoading = true;
  private isCategoryMasterDataGridSettingLoading = true;
  private categoryMappingDataGridSetting: GridSetting = new GridSetting();
  private categoryMasterDataGridSetting: GridSetting = new GridSetting();

  constructor(
	private categoryDataService: CategoryDataService,
	private userService: UserApi,
	private filterBuilder: FilterBuilderService,
	private loaderService: LoaderService,
  ) { }

  ngOnInit() {
	this.loaderService.show();
	this.currentUser = this.userService.logonUser;
	if (!this.currentUser) {
		return;
	}
	this.categoryMappingDataGridSettings = {
		PageName: this.pageName,
		GridName: this.mappingGridName,
		OwnedByUserId: this.currentUser.UserId
	};
	this.userService.getGridSettings(this.categoryMappingDataGridSettings)
		.pipe(take(1))
		.subscribe((settings) => {
		if (settings && settings.Settings) {
			if (typeof settings.Settings === 'string') {
			this.categoryMappingDataGridSetting = JSON.parse(settings.Settings);
			} else {
			this.categoryMappingDataGridSetting = settings.Settings;
			}
		} else {
			this.categoryMappingDataGridSettings.Settings = JSON.stringify({autosave: true});
			this.updateGridSettings(this.categoryMappingDataGridSettings);
		}
		this.loadCategoryMappingDataGridState();
		});

	this.categoryMasterDataGridSettings = {
		PageName: this.pageName,
		GridName: this.masterGridName,
		OwnedByUserId: this.currentUser.UserId
	};

	this.userService.getGridSettings(this.categoryMasterDataGridSettings)
		.pipe(take(1))
		.subscribe((settings) => {
		if (settings && settings.Settings) {
			if (typeof settings.Settings === 'string') {
			this.categoryMasterDataGridSetting = JSON.parse(settings.Settings);
			} else {
			this.categoryMasterDataGridSetting = settings.Settings;
			}
		} else {
			this.categoryMasterDataGridSettings.Settings = JSON.stringify({autosave: true});
			this.updateGridSettings(this.categoryMasterDataGridSettings);
		}
		this.loadCategoryMasterDataGridState();
		});

	this.loadColumnHeaderFilterData();
  }

  private loadCategoryMappingDataSource() {
	this.isCategoryMappingDataGridSettingLoading = false;
	this.categoryMappingDataSource = new CustomStore({
		key: 'CategoryMappingId',
		load: (loadOptions: any) => {
		this.isCategoryMappingDataLoading = true;
		this.loaderService.show();
		const filter = this.formatFilterOperators(loadOptions.filter);
		const finalOptions: LoadOptions = loadOptions;
		finalOptions.filter = filter;
		finalOptions.extraParams = this.extraFilteringOptions;

		return this.categoryDataService.getCategoryMappingGrid(finalOptions)
			.toPromise()
			.then((data: any) => {
			this.isCategoryMappingDataLoading = false;
			this.loaderHandler();
			return {
				data: data.data,
				totalCount: data.totalCount,
				summary: data.summary,
				groupCount: data.groupCount
			};
			})
			.catch(error => {
			this.isCategoryMappingDataLoading = false;
			this.loaderHandler();
			throw new Error('Data Loading Error');
			});
		}
	});
	this.isLoadCategoryMappingGridDataSource = true;
  }

  private loadCategoryMappingDataGridState() {
	const categoryMappingDataGridState: GridState = {
		PageName: this.pageName,
		GridName: this.mappingGridName,
		OwnedByUserId: this.currentUser.UserId
	};
	this.userService.getGridState(categoryMappingDataGridState)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((state) => {
		if (state) {
			this.currentCategoryMappingDataGridState = state;
		} else {
			this.currentCategoryMappingDataGridState = {
			PageName: this.pageName,
			GridName: this.mappingGridName,
			OwnedByUserId: this.currentUser.UserId,
			State: null
			};
		}
		this.loadCategoryMappingDataSource();
		});
  }

  public loadMappingState = () => {
	return this.currentCategoryMappingDataGridState.State ? JSON.parse(this.currentCategoryMappingDataGridState.State) : null;
  }

  public saveMappingState = (state) => {
	if (this.categoryMappingDataGridSetting) {
		let gridSetting: GridSetting;
		if (typeof this.categoryMappingDataGridSetting === 'string') {
		gridSetting = JSON.parse(this.categoryMappingDataGridSetting);
		} else {
		gridSetting = this.categoryMappingDataGridSetting;
		}
		if (gridSetting.autosave) {
		this.currentCategoryMappingDataGridState.State = JSON.stringify(state);
		this.updateGridState(this.currentCategoryMappingDataGridState);
		}
	}
  }

  private loadCategoryMasterDataSource() {
	this.isCategoryMasterDataGridSettingLoading = false;
	this.categoryMasterDataSource = new CustomStore({
		key: 'RowId',
		load: (loadOptions: any) => {
		this.isCategoryMasterDataLoading = true;
		this.loaderService.show();
		const filter = this.formatFilterOperators(loadOptions.filter);
		const finalOptions: LoadOptions = loadOptions;
		finalOptions.filter = filter;

		return this.categoryDataService.getCategoryMasterGrid(finalOptions)
			.toPromise()
			.then((data: any) => {
			this.isCategoryMasterDataLoading = false;
			this.loaderHandler();
			return {
				data: data.data,
				totalCount: data.totalCount,
				summary: data.summary,
				groupCount: data.groupCount
			};
			})
			.catch(error => {
			this.isCategoryMasterDataLoading = false;
			this.loaderHandler();
			throw new Error('Data Loading Error');
			});
		}
	});
	this.isLoadCategoryMasterGridDataSource = true;
  }

  private loadCategoryMasterDataGridState() {
	const categoryMasterDataGridState: GridState = {
		PageName: this.pageName,
		GridName: this.masterGridName,
		OwnedByUserId: this.currentUser.UserId
	};
	this.userService.getGridState(categoryMasterDataGridState)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((state) => {
		if (state) {
			this.currentCategoryMasterDataGridState = state;
		} else {
			this.currentCategoryMasterDataGridState = {
			PageName: this.pageName,
			GridName: this.masterGridName,
			OwnedByUserId: this.currentUser.UserId,
			State: null
			};
		}
		this.loadCategoryMasterDataSource();
		});
  }

  public loadMasterState = () => {
	return this.currentCategoryMasterDataGridState.State ? JSON.parse(this.currentCategoryMasterDataGridState.State) : null;
  }

  public saveMasterState = (state) => {
	if (this.categoryMasterDataGridSetting) {
		let gridSetting: GridSetting;
		if (typeof this.categoryMasterDataGridSetting === 'string') {
		gridSetting = JSON.parse(this.categoryMasterDataGridSetting);
		} else {
		gridSetting = this.categoryMasterDataGridSetting;
		}
		if (gridSetting.autosave) {
		this.currentCategoryMasterDataGridState.State = JSON.stringify(state);
		this.updateGridState(this.currentCategoryMasterDataGridState);
		}
	}
  }

  private updateGridSettings(gridSettings: GridSettings) {
	gridSettings.SavedOn = new Date();
	this.userService.setGridSettings(gridSettings).pipe(take(1)).subscribe();
  }

  private updateGridState(gridState: GridState) {
	gridState.SavedOn = new Date();
	this.userService.setGridState(gridState).pipe(take(1)).subscribe();
  }

  private loadColumnHeaderFilterData() {
	this.dataProviderCodeHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'DataProviderCode';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.companyNamedHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CompanyName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.mappingModeHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'MappingModeName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.totalCountHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'TotalCount';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.newHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'NewCount';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.mapHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'MapCount';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.saveHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'SaveCount';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.omitHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'OmitCount';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.inconsistencyHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'IsInconsistentMappingMessage';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.subcategoryFunctionNameHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'SubcategoryFunctionName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.subcategoryFormNameHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'SubcategoryFormName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.categoryIdHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CategoryId';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.createdByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CreatedByUserFullNameSortableOrPrincipalName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.createdOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CreatedOn';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.modifiedByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ModifiedByUserFullNameSortableOrPrincipalName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.modifiedOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ModifiedOn';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.reviewedByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ReviewedByUserFullNameSortableOrPrincipalName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.reviewedOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ReviewedOn';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMapping(loadOptions);
		}
	});

	this.masterCategoryNameDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CategoryName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMaster(loadOptions);
		}
	});

	this.masterSubcategoryFunctionNameHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'SubcategoryFunctionName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMaster(loadOptions);
		}
	});

	this.masterSubcategoryFormNameHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'SubcategoryFormName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCategoryMaster(loadOptions);
		}
	});
  }

  getHeaderFiltersCategoryMapping(loadingOption) {
	return this.categoryDataService.getHeaderFiltersCategoryMapping(loadingOption)
		.toPromise()
		.catch(error => { throw new Error('Data Loading Error'); });
  }

  getHeaderFiltersCategoryMaster(loadingOption) {
	return this.categoryDataService.getHeaderFiltersCategoryMaster(loadingOption)
		.toPromise()
		.catch(error => { throw new Error('Data Loading Error'); });
  }

  selectionMappingDataChangedHandler() {
	if (this.categoryMappingDataGrid.instance.getSelectedRowsData()[0]) {
		this.categoryMappingSelectedRowMap = this.categoryMappingDataGrid.instance.getSelectedRowsData()[0];
	}
  }

  /**
   * Context menu for Category mapping data grid
   * @param event
   */
   categoryMappingDataGridContextMenu(e) {
	if (e.target === 'content') {
		// e.items can be undefined
		if (!e.items) { e.items = []; }

		// Add a custom menu item
		e.items.push(
		{
			text: 'Refresh',
			icon: 'refresh',
			onItemClick: () => {
			this.refreshCategoryMappingDataGrid();
			}
		},
		{
			text: 'Grid Settings',
			items: [
			{
				text: this.categoryMappingDataGridSetting.autosave ? 'Disable autosave' : 'Enable autosave',
				onItemClick: () => {
				this.categoryMappingDataGridSetting.autosave = !this.categoryMappingDataGridSetting.autosave;
				this.categoryMappingDataGridSettings.Settings = JSON.stringify(this.categoryMappingDataGridSetting);
				this.updateGridSettings(this.categoryMappingDataGridSettings);
				}
			}
			]
		}
		);
	}
  }

  customizeCategoryMappingId(cellInfo) {
	const idStr = 'CaMp_000000000';
	const cellValue = String(cellInfo.value);
	const index = idStr.length - cellValue.length;
	const finalText = idStr.slice(0, index) + cellValue;
	return finalText;
  }

  extraFilteringOptionsChange(columnName, event) {
	if (event) {
		const option = this.extraFilteringOptions.find(item => item.column === columnName);
		option.value = event.value;
		this.categoryMappingDataGrid.instance.deselectAll();
		this.refreshCategoryMappingDataGrid();
	}
  }

  refreshCategoryMappingDataGrid() {
	this.mappingDataGridCurrentPage = this.categoryMappingDataGrid.instance.pageIndex();
	this.categoryMappingDataGrid.instance.refresh();
  }

  selectionMasterDataChangedHandler() {
	if (this.categoryMasterDataGrid.instance.getSelectedRowsData()[0]) {
		this.categoryMasterSelectedRowMap = this.categoryMasterDataGrid.instance.getSelectedRowsData()[0];
	}
  }

  /**
   * Context menu for Category master data grid
   * @param event
   */
   categoryMasterDataGridContextMenu(e) {
	if (e.target === 'content') {
		// e.items can be undefined
		if (!e.items) { e.items = []; }

		// Add a custom menu item
		e.items.push(
		{
			text: 'Refresh',
			icon: 'refresh',
			onItemClick: () => {
			this.refreshCategoryMasterDataGrid();
			}
		},
		{
			text: 'Grid Settings',
			items: [
			{
				text: this.categoryMasterDataGridSetting.autosave ? 'Disable autosave' : 'Enable autosave',
				onItemClick: () => {
				this.categoryMasterDataGridSetting.autosave = !this.categoryMasterDataGridSetting.autosave;
				this.categoryMasterDataGridSettings.Settings = JSON.stringify(this.categoryMasterDataGridSetting);
				this.updateGridSettings(this.categoryMasterDataGridSettings);
				}
			}
			]
		}
		);
	}
  }

  customizeCategoryMasterId(cellInfo) {
	const idStr = 'CaR_000';
	const cellValue = String(cellInfo.value);
	const index = idStr.length - cellValue.length;
	const finalText = idStr.slice(0, index) + cellValue;
	return finalText;
  }

  refreshCategoryMasterDataGrid() {
	this.categoryMasterDataGrid.instance.refresh();
  }

  onReady(e) {
	const grid = e.component;
	const selection = grid.getSelectedRowKeys();
	if (selection.length === 0) {
		grid.selectRowsByIndexes([0]);
	}
  }

  formatFilterOperators(rawFilter: any[]) {
	if (!rawFilter) {
		return null;
	}
	const filterData = this.filterBuilder.handleNotFilterFormatting(rawFilter);
	return this.filterBuilder.filterFormatting(filterData);
  }

  // actions
  omit(categoryMappingId) {
	const mappingData: CategoryMappingOption = {};
	mappingData.MappingModeId = MappingMode.Omitted;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.CategoryId = categoryMappingId;
	mappingData.ActionType = MappingAction.Omit;
	this.categoryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCategoryMappingDataGrid();
		});
  }

  save(categoryMappingId) {
	const mappingData: CategoryMappingOption = {};
	mappingData.MappingModeId = MappingMode.Saved;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.ActionType = MappingAction.Save;
	mappingData.CategoryId = categoryMappingId;
	this.categoryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCategoryMappingDataGrid();
		});
  }

  deactivate(categoryMappingId) {
	const mappingData: CategoryMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.ActionType = MappingAction.Deactivate;
	mappingData.CategoryId = categoryMappingId;
	this.categoryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCategoryMappingDataGrid();
		});
  }

  accept(categoryMappingId: any) {
	const mappingData: CategoryMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.ActionType = MappingAction.Accept;
	mappingData.CategoryId = categoryMappingId;
	this.categoryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCategoryMappingDataGrid();
		});
  }

  review(categoryMappingId: any) {
	const mappingData: CategoryMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.ActionType = MappingAction.Review;
	mappingData.CategoryId = categoryMappingId;
	this.categoryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCategoryMappingDataGrid();
		});
  }

  map(categoryId, subcategoryFormRelationId, subcategoryFunctionRelationId) {
	const mappingData: CategoryMappingOption = {};
	mappingData.MappingModeId = MappingMode.Mapped;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.ActionType = MappingAction.Map;
	mappingData.CategoryId = categoryId;
	mappingData.SubcategoryFormRelationId = subcategoryFormRelationId;
	mappingData.SubcategoryFunctionRelationId = subcategoryFunctionRelationId;
	this.categoryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCategoryMappingDataGrid();
		});
  }

  private loaderHandler() {
	if (
		!this.isCategoryMappingDataLoading &&
		!this.isCategoryMasterDataLoading &&
		!this.isCategoryMappingDataGridSettingLoading &&
		!this.isCategoryMasterDataGridSettingLoading) {
		this.loaderService.hide();
	}
  }

  ngOnDestroy() {
	this.loaderService.hide();
	this._destroyed$.next(null);
	this._destroyed$.complete();
  }
}
